import { t } from "@hakili/i18n";
import { Button, ERROR_FIELD_BORDER, ERROR_TEXT } from "@hakili/ui";
import { AlertTriangle } from "lucide-react";
import type { ReactElement } from "react";

import { HeldCompounds } from "./held-compounds.js";

/** The ten things a dealer's screens read and may fail to read (DEALER-READ-FAILURE-MONEY, ruling of 30 October 2026, and -LISTS, of 31 October). */
export type ReadFailureWhat =
  | "dealer.lecture.coordonnees"
  | "dealer.lecture.reglement"
  | "dealer.lecture.ecart"
  | "dealer.lecture.controle"
  | "dealer.lecture.rapport"
  | "dealer.lecture.alertes"
  | "dealer.lecture.chiffres"
  | "dealer.lecture.pieces"
  | "dealer.lecture.papiers"
  | "dealer.lecture.dossiers"
  | "dealer.lecture.dossier"
  | "dealer.lecture.estimation";

/**
 * `A4`, DEALER-READ-FAILURE-MONEY: **a read that failed says so, and a failed figure is never « 0 ».**
 *
 * « Impossible de lire {quoi} pour le moment. », then the door's own message (`messageOf`, which already falls back to « La connexion a
 * échoué. »), then « Réessayer ». The words are the architect's; the screen that owns the read decides what it hides while the read stands
 * failed (a balance is not drawn, a bank-account form is not offered).
 *
 * **The form is ONE for all platforms** (the architect's ruling of 31 October 2026, after the designer's look): the bank's D2 frame 210,
 * the warn notice with the alert icon LEFT of the title and « Réessayer » as the kit's SECONDARY BUTTON inside it.
 */
export function ReadFailure(props: {
  readonly what: ReadFailureWhat;
  readonly message: string;
  readonly onRetry: () => void;
  readonly testId: string;
  readonly retryTestId: string;
}): ReactElement {
  return (
    <div
      role="alert"
      className={`flex items-start gap-3 rounded-xl border ${ERROR_FIELD_BORDER} bg-white px-4 py-3 text-sm ${ERROR_TEXT}`}
      data-testid={props.testId}
    >
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="m-0 font-semibold">{t("dealer.lecture.echec").split("{quoi}").join(t(props.what))}</p>
        <p className="m-0 mt-1">
          <HeldCompounds text={props.message} />
        </p>
        <div className="mt-3">
          <Button variant="secondary" size="sm" onClick={props.onRetry} data-testid={props.retryTestId}>
            {t("common.action.reessayer")}
          </Button>
        </div>
      </div>
    </div>
  );
}
